<template>
  <div
    class="report"
    v-if="report !== null && report != undefined && report.membersWithSoloCount > 1"
  >
    <div class="container maxWidth-md">
      <h1>Organisation Report for {{ report.name }}</h1>
      <hr class="hr-magazine hr-magazine-thick hr-magazine-left hr-magazine-short" />
    </div>
    <div class="row">
      <organisation-heads
        :id="id"
        :hasViewpoints="report.membersWithViewpointsCount > 1"
      ></organisation-heads>
    </div>
    <div class="row">
      <h2>Top 2 Best-fit Leader types</h2>
      <div class="col-md-6">
        <donut-chart
          title="Solo"
          :membersCount="report.membersWithSoloCount"
          description="Percentage of best fit leader types in the
    organisation based on solo"
          :values="report.organisationSoloReport.bestFitLeadersDistribution"
          :leaderTypeDetails="report.leaderTypes"
        ></donut-chart>
      </div>
      <div class="col-md-6" v-if="report.membersWithViewpointsCount > 1">
        <donut-chart
          title="Viewpoints"
          :membersCount="report.membersWithViewpointsCount"
          description="Percentage of best fit leader types in the
    organisation based on viewpoints"
          :values="report.organisationViewpointsReport.bestFitLeadersDistribution"
          :leaderTypeDetails="report.leaderTypes"
        ></donut-chart>
      </div>
    </div>
    <div class="row">
      <h2>Bottom 2 Least-fit Leader types</h2>
      <div class="col-md-6">
        <donut-chart
          title="Solo"
          :membersCount="report.membersWithSoloCount"
          description="Percentage of best fit leader types in the
    organisation based on solo"
          :values="report.organisationSoloReport.worstFitLeadersDistribution"
          :leaderTypeDetails="report.leaderTypes"
        ></donut-chart>
      </div>
      <div class="col-md-6" v-if="report.membersWithViewpointsCount > 1">
        <donut-chart
          title="Viewpoints"
          :membersCount="report.membersWithViewpointsCount"
          description="Percentage of best fit leader types in the
    organisation based on viewpoints"
          :values="report.organisationViewpointsReport.worstFitLeadersDistribution"
          :leaderTypeDetails="report.leaderTypes"
        ></donut-chart>
      </div>
    </div>
    <div class="row" style="margin-top: 3rem">
      <strengths-list-pair
        :soloRatings="sortedSolos"
        :viewpointsRatings="sortedViewpoints"
      ></strengths-list-pair>
    </div>
  </div>
  <div
    v-else-if="
      error || (report !== null && report != undefined && report.membersWithSoloCount <= 1)
    "
  >
    <h2>Cannot create a report for this organisation</h2>
  </div>
  <div v-else>
    <h2>Loading...</h2>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import { OrganisationReportApiModel, StrengthItemRatingApiModel } from "../../api-models";
import DonutChart from "../../components/admin/report-templates/donut-chart.vue";
import StrengthsListPair from "../../components/strengths-lists/strengths-list-pair.vue";
import OrganisationHeads from "../../components/admin/organisation/organisation-heads.vue";

const props = defineProps<{
  id: string;
}>();

const report = ref<OrganisationReportApiModel | null>(null);
const error = ref(false);

const sortedSolos = computed(() => {
  return (
    report.value?.organisationSoloReport?.strengths
      .slice()
      .sort((a, b) => (a.zScore != null && b.zScore != null ? b.zScore - a.zScore : 0)) ?? []
  );
});

const sortedViewpoints = computed((): StrengthItemRatingApiModel[] => {
  return (
    report.value?.organisationViewpointsReport?.strengths
      .slice()
      .sort((a, b) => (a.zScore != null && b.zScore != null ? b.zScore - a.zScore : 0)) ?? []
  );
});

onMounted(async () => {
  axios
    .get(`/api/organisationreport/${props.id}`)
    .then((resp) => {
      report.value = resp.data;
    })
    .catch(() => {
      error.value = true;
    });
});
</script>

<style scoped lang="less">
.container {
  margin-bottom: 5rem;
}

.row {
  padding: 20px 10%;
}

.report .row:nth-child(even) {
  background: #f5f6f7;
}
</style>
